
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { EmailData } from './types';
import { smtpSchema } from './SmtpTestForm';
import * as z from 'zod';

export const useEmailViewer = (email: string) => {
  const [emails, setEmails] = useState<EmailData[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingSmtpTest, setSendingSmtpTest] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const { toast } = useToast();

  const simulateEmails = () => {
    const origin = window.location.origin;
    const demoEmails: EmailData[] = [
      {
        type: 'confirm_signup',
        email: email,
        subject: 'Confirm your email address',
        url: `${origin}/auth?confirmation=demo&email=${encodeURIComponent(email)}`,
        token: 'demo-verification-token-123456',
        content: 'This is a simulated email verification link. In a real environment, this would be a genuine Supabase authentication link.'
      },
      {
        type: 'reset_password',
        email: email,
        subject: 'Reset your password',
        url: `${origin}/auth?view=resetPassword&demo=true`,
        token: 'demo-reset-token-654321',
        content: 'This is a simulated password reset link. In a real environment, this would be a genuine Supabase authentication link.'
      }
    ];

    return demoEmails;
  };

  const fetchEmails = async () => {
    try {
      setLoading(true);
      
      console.log("Attempting to fetch test emails for:", email);
      
      const demoEmails = simulateEmails();
      setEmails(demoEmails);
      
      toast({
        title: "Demo Mode",
        description: "Showing simulated test emails. These are demonstrations of what real auth emails look like.",
      });
    } catch (error) {
      console.error("Error fetching test emails:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch test emails. See console for details.",
      });
    } finally {
      setLoading(false);
    }
  };

  const clearEmails = async () => {
    try {
      setLoading(true);
      setEmails([]);
      toast({
        title: "Success",
        description: "All test emails cleared",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to clear test emails",
      });
    } finally {
      setLoading(false);
    }
  };

  const sendSmtpTestEmail = async (formData: z.infer<typeof smtpSchema>) => {
    try {
      setSendingSmtpTest(true);
      
      const { hostname, port, username, password, tls, subject, content } = formData;
      
      const { data: sessionData } = await supabase.auth.getSession();
      
      console.log("Preparing to call edge function with SMTP test data");
      
      const { data, error } = await supabase.functions.invoke('test-email-handler', {
        body: {
          type: 'smtp_test',
          email: email,
          smtp: {
            hostname,
            port,
            username,
            password,
            tls
          },
          subject: subject || 'SMTP Test Email',
          content: content || 'This is a test email from your SMTP server configuration.'
        }
      });
      
      if (error) {
        console.error("Edge function error:", error);
        throw new Error(`Edge function error: ${error.message || JSON.stringify(error)}`);
      }
      
      console.log("Edge function response:", data);
      
      if (data.error) {
        throw new Error(`SMTP error: ${data.error} - ${data.details || ''}`);
      }
      
      const testEmail = {
        type: 'smtp_test' as const,
        email: email,
        subject: subject || 'SMTP Test Email',
        content: content || 'This is a test email from your SMTP server configuration.',
        smtp: {
          hostname,
          port,
          username,
          tls
        }
      };
      
      setEmails([testEmail, ...emails]);
      setActiveTab('smtp_test');
      
      if (hostname === 'smtp.resend.com') {
        toast({
          title: "SMTP Connection Successful",
          description: "Your Resend.com SMTP configuration was verified successfully. This is a simulated test - no actual email has been sent to your inbox.",
        });
      } else {
        toast({
          title: "SMTP Connection Successful",
          description: "Your SMTP settings were verified successfully. Check your email inbox to confirm delivery.",
        });
      }
    } catch (error: any) {
      console.error("Error sending SMTP test email:", error);
      
      if (error.message && error.message.includes("No active session found")) {
        toast({
          variant: "destructive",
          title: "Authentication Required",
          description: "You can continue testing without signing in, but this feature works best when signed in.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error Sending Email",
          description: error.message || "Failed to send SMTP test email. See console for details.",
        });
      }
    } finally {
      setSendingSmtpTest(false);
    }
  };

  useEffect(() => {
    console.log("TestEmailViewer mounted with email:", email);
    if (email) {
      fetchEmails();
    }
  }, [email]);

  return {
    emails,
    loading,
    sendingSmtpTest,
    activeTab,
    setActiveTab,
    fetchEmails,
    clearEmails,
    sendSmtpTestEmail
  };
};
